/* script */
//
//
//
//
//
//
//
//
//
//
//
//

// Utils
import { JSONEditor, createAjvValidator, toJSONContent } from 'vanilla-jsoneditor';

const __vue_script__ = {
    name: 'JsonEditor',

    props: {
        eventId: {
            type: String,
            required: true
        },

        value: {
            type: [String, Object, Array],
            default: null
        },

        emitOnChange: {
            type: Boolean,
            default: false
        },

        scrollTo: {
            type: String,
            default: null
        },

        /** 'tree' | 'text' | 'table'. Open the editor in 'tree' mode (default), 'table' mode, or 'text' mode (formerly: code mode). */
        mode: {
            type: String,
            default: 'text'
        },

        /** Show the main menu bar. Default value is true. */
        mainMenuBar: {
            type: Boolean,
            default: true
        },

        /** Show the navigation bar with, where you can see the selected path and navigate through your document from there. Default value is true. */
        navigationBar: {
            type: Boolean,
            default: true
        },

        /** Show a status bar at the bottom of the 'text' editor, showing information about the cursor location and selected contents. Default value is true. */
        statusBar: {
            type: Boolean,
            default: true
        },

        /** Open the editor in read-only mode: no changes can be made, non-relevant buttons are hidden from the menu, and the context menu is not enabled. Default value is false. */
        readOnly: {
            type: Boolean,
            default: false
        },

        /** Number of spaces use for indentation when stringifying JSON, or a string to be used as indentation like '\t' to use a tab as indentation, or ' ' to use 4 spaces (which is equivalent to configuring indentation: 4). See also property tabSize. */
        indentation: {
            type: [Number, String],
            default: undefined
        },

        /** When indentation is configured as a tab character (indentation: '\t'), tabSize configures how large a tab character is rendered. Default value is 4. Only applicable to text mode. */
        tabSize: {
            type: Number,
            default: undefined
        },

        /** False by default. When true, control characters like newline and tab are rendered as escaped characters \n and \t. Only applicable for 'tree' mode, in 'text' mode control characters are always escaped. */
        escapeControlCharacters: {
            type: Boolean,
            default: false
        },

        /** False by default. When true, unicode characters like ☎ and 😀 are rendered escaped like \u260e and \ud83d\ude00. */
        escapeUnicodeCharacters: {
            type: Boolean,
            default: false
        },

        /** True by default. Only applicable to 'table' mode. When true, nested object properties will be displayed each in their own column, with the nested path as column name. When false, nested objects will be rendered inline, and double-clicking them will open them in a popup. */
        flattenColumns: {
            type: Boolean,
            default: true
        },

        /** function (json: JSONValue): ValidationError[]. Validate the JSON document. For example use the built-in JSON Schema validator powered by Ajv: */
        validator: {
            type: Function,
            default: undefined
        },

        /** JSONParser = JSON. Only applicable when a validator is provided. This is the same as parser, except that this parser is used to parse the data before sending it to the validator. Configure a custom JSON parser that is used to parse JSON before passing it to the validator. By default, the built-in JSON parser is used. When passing a custom validationParser, make sure the output of the parser is supported by the configured validator. So, when the validationParser can output bigint numbers or other numeric types, the validator must also support that. In tree mode, when parser is not equal to validationParser, the JSON document will be converted before it is passed to the validator via validationParser.parse(parser.stringify(json)). */
        validationParser: {
            type: Object,
            default: undefined
        },

        /** JSONPathParser. An optional object with a parse and stringify method to parse and stringify a JSONPath, which is an array with property names. The pathParser is used in the path editor in the navigation bar, which is opened by clicking the edit button on the right side of the navigation bar. The pathParser.parse function is allowed to throw an Error when the input is invalid. By default, a JSON Path notation is used, which looks like $.data[2].nested.property. Alternatively, it is possible to use for example a JSON Pointer notation like /data/2/nested/property or something custom-made. Related helper functions: parseJSONPath and stringifyJSONPath, parseJSONPointer and compileJSONPointer. */
        pathParser: {
            type: Object,
            default: undefined
        },

        /** Configure one or multiple query language that can be used in the Transform modal. The library comes with three languages: */
        queryLanguages: {
            type: Array,
            default: undefined
        },

        /** The id of the currently selected query language. */
        queryLanguageId: {
            type: String,
            default: undefined
        },

        setContentOnValueChanges: {
            type: Boolean,
            default: false
        },
    },

    data() {
        return {
            editor: null,
            editing: Boolean(this.value) && this.value.hasOwnProperty('_id'),
            hasChanged: false,
            valid: false,
            schema: null,
            selectionListener: null,
            ready: false,
            configured: false
        };
    },

    computed: {
        canSave() {
            return this.hasChanged && this.valid;
        },

        isSharable() {
            return this.value?._id;
        }
    },

    watch: {
        value: {
            deep: true,
            handler(data) {
                if (this.setContentOnValueChanges) {
                    this.editor.set({ json: data });
                }

                if (this.emitOnChange && this.configured) return;

                this.editor.update({ json: data });
                this.$nextTick(() => {
                    this.hasChanged = false;
                    this.configured = true;
                });
            }
        },

        hasChanged(value) {
            this.$emit('changed', value);
        },

        scrollTo() {
            this.expandNode();
        }
    },

    async mounted() {
        const props = {
            ...this.pickDefinedProps(),
            content: { json: this.value },
            indentation: 4,
            onError: this.onError.bind(this),
            onChange: this.onChange.bind(this),
            onChangeMode: this.onChangeMode.bind(this),
            onClassName: this.onClassName.bind(this),
            onRenderMenu: this.onRenderMenu.bind(this),
            onChangeQueryLanguage: this.onChangeQueryLanguage.bind(this),
            onFocus: this.onFocus.bind(this),
            onBlur: this.onBlur.bind(this),
        };

        if (!this.hasOwnProperty('mainMenuBar')) {
            props.mainMenuBar = true;
        }

        if (this.value?.fp_type) {
            this.schema = await this.$services.metadata.getSchemaForType(this.eventId, this.value.fp_type);
            props.validator = createAjvValidator({ schema: this.schema });
        }

        this.editor = new JSONEditor({ target: this.$refs['editor'], props });
        this.expandNode();
        console.log('[JsonEditor] Create editor', this.editor);

        /** @type {HTMLDivElement} */
        const editor = this.$refs['editor'];
        this.selectionListener = this.onSelect.bind(this);
        editor.addEventListener('dblclick', this.selectionListener);

        if (this.emitOnChange) {
            this.editor.update({ json: this.value });
        }

        this.$nextTick(() => {
            this.ready = true;
        });
    },

    updated() {
        const props = this.pickDefinedProps();
        console.log('[JsonEditor] Update props', props);
        this.editor.updateProps(props);
    },

    beforeUnmount() {
        console.log('[JsonEditor] Destroy editor');
        this.editor.destroy();
        this.editor = null;
        if (this.selectionListener) {
            const editor = this.$refs['editor'];
            editor.removeEventListener('dblclick', this.selectionListener);
        }
    },

    methods: {
        pickDefinedProps() {
            const props = {};
            const names = Object.keys(this.$props).filter(p => !['value', 'eventId', 'scrollTo', 'emitOnChange'].includes(p));
            for (const propName of names) {
                if (this[propName] !== undefined) {
                    props[propName] = this[propName];
                }
            }
            return props;
        },

        async expandNode() {
            if (!this.scrollTo || !this.editor) {
                return;
            }

            this.editor.updateProps({ mode: this.mode || 'tree' });

            this.$nextTick(() => {
                this.editor.expand(path => {
                    const [child] = path;
                    return !child || child === this.scrollTo;
                });

                this.editor.scrollTo([this.scrollTo]);
            });
        },

        /**
         * Callback fired when an error occurs.
         * Default implementation is to log an error in the console
         * and show a simple alert message to the user.
         *
         * @param {Error} error
         */
        onError(error) {
            this.$emit('error', error);
        },

        /**
         * The callback which is invoked on every change of the contents,
         * both changes made by a user and programmatic changes made via
         * methods like .set(), .update(), or .patch().
         *
         * The returned content is sometimes of type { json }, and
         * sometimes of type { text }. Which of the two is returned
         * depends on the mode of the editor, the change that is applied,
         * and the state of the document (valid, invalid, empty).
         *
         * Please be aware that { text } can contain invalid JSON: whilst
         * typing in text mode, a JSON document will be temporarily invalid,
         * like when the user is typing a new string. The parameter
         * patchResult is only returned on changes that can be represented
         * as a JSON Patch document, and for example not when freely typing in text mode.
         *
         * @param {import('vanilla-jsoneditor').Content} content
         * @param {import('vanilla-jsoneditor').Content} previousContent
         * @param {{ contentErrors: ContentErrors, patchResult: JSONPatchResult | null }} changeStatus
         */
        onChange(content, previousContent, changeStatus) {
            this.hasChanged = true;
            const errors = changeStatus.contentErrors || {};
            this.valid = !errors.parseError && (!errors.validationErrors || errors.validationErrors.length === 0);

            if (this.valid) {
                const { json } = toJSONContent(content);
                if (this.emitOnChange) {
                    this.$emit('input', json);
                } else {
                    this.$emit('change', json, previousContent, changeStatus);
                }
            }
        },

        /**
         * Invoked when the mode is changed.
         *
         * @param {'tree' | 'text' | 'table'} mode
         */
        onChangeMode(mode) {
            this.$emit('change-mode', mode);
        },

        /**
         * Add a custom class name to specific nodes, based on their path and/or
         * value.
         *
         * Note that in the custom class, you can override CSS variables
         * like --jse-contents-background-color to change the styling of
         * a node, like the background color. Relevant variables are:
         *   --jse-contents-background-color
         *   --jse-selection-background-color
         *   --jse-selection-background-inactive-color
         *   --jse-hover-background-color
         *   --jse-context-menu-pointer-hover-background
         *   --jse-context-menu-pointer-background
         *   --jse-context-menu-pointer-background-highlight
         *   --jse-collapsed-items-background-color
         *   --jse-collapsed-items-selected-background-color
         *
         * To adjust the text color of keys or values, the color of the
         * classes .jse-key and .jse-value can be overwritten.
         *
         * @param {Path} path
         * @param {any} value
         */
        onClassName(path, value) {
            this.$emit('class-name', path, value);
        },

        /**
         * Callback which can be used to make changes to the menu items.
         *
         * New items can be added, or existing items can be removed or reorganized.
         * When the function returns undefined, the original items will be applied.
         * Using the context values mode and modal, different actions can be taken
         * depending on the mode of the editor and whether the editor is rendered
         * inside a modal or not.
         *
         * @param {import('vanilla-jsoneditor').MenuItem} items
         * @param {{ mode: 'tree' | 'text' | 'table', modal: boolean }} context
         */
        onRenderMenu(items, context) {
            this.$emit('render-menu', items, context);
        },

        /**
         * Callback function invoked when the user changes the selected
         * query language in the TransformModal via the configuration
         * button top right.
         *
         * @param {string} queryLanguageId
         */
        onChangeQueryLanguage(queryLanguageId) {
            this.$emit('change-query-language', queryLanguageId);
        },

        /**
         * Callback fired when the editor got focus.
         */
        onFocus() {
            this.$emit('focus');
        },

        /**
         * Callback fired when the editor lost focus.
         */
        onBlur() {
            this.$emit('blur');
        },

        /**
         * Callback fired on text selection
         */
        onSelect() {
            const selection = window.getSelection().toString().trim();

            if (selection.length) {
                this.$emit('select', selection);
            }
        },

        /**
         * Sends the data to the receiving
         */
        save() {
            const content = this.editor.get();
            const { json } = toJSONContent(content);

            this.$emit('input', json);
        },

        async share() {
            try {
                const { origin, pathname } = window.location;
                const url = `${origin}${pathname}?document=${this.value._id}`;
                await navigator.clipboard.writeText(url);
                this.$notify({
                    group: 'flashes',
                    text: this.$i18n.t('dev_tools.document_editor.messages.document_url_copied', [this.email]).toString(),
                    type: 'success',
                    duration: 5000
                });
            } catch (error) {
                console.error('[JsonEditor] Could not copy document URL to clipboard', error.message);
                this.$notify({
                    group: 'flashes',
                    text: this.$i18n.t('dev_tools.document_editor.messages.document_url_not_copied', [this.email]).toString(),
                    type: 'error',
                    duration: 5000
                });
            }
        }

    }
};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"json-editor"},[(_vm.ready && _vm.mainMenuBar)?_c('div',{staticClass:"actions"},[_c('button',{directives:[{name:"tooltip",rawName:"v-tooltip",value:(_vm.$t('general.reload')),expression:"$t('general.reload')"}],staticClass:"btn btn-icon single",attrs:{"type":"button","disabled":!_vm.editing},on:{"click":function($event){return _vm.$emit('reset')}}},[_c('i',{staticClass:"ic-refresh-cw"})]),_c('button',{directives:[{name:"tooltip",rawName:"v-tooltip",value:(_vm.$t('general.save') ),expression:"$t('general.save') "}],staticClass:"btn btn-icon single",attrs:{"type":"button","disabled":!_vm.canSave},on:{"click":function($event){return _vm.save()}}},[_c('i',{staticClass:"ic-save"})])]):_vm._e(),_c('div',{staticClass:"second-toolbar"},[(_vm.isSharable)?_c('button',{directives:[{name:"tooltip",rawName:"v-tooltip",value:(_vm.$t('dev_tools.document_editor.share')),expression:"$t('dev_tools.document_editor.share')"}],staticClass:"btn btn-icon single",attrs:{"type":"button"},on:{"click":function($event){return _vm.share()}}},[_c('i',{staticClass:"ic-share-2"})]):_vm._e()]),_c('div',{ref:"editor",staticClass:"editor"})])}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "JsonEditor.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__